import { useState, useEffect, useRef } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { X, Plus } from "lucide-react";

const SYSTEM_TAGS = [
  { slug: "logo", label: "Logo" },
  { slug: "palette", label: "Palet" },
  { slug: "thumbnail-ref", label: "Miniatür istinad" },
  { slug: "character", label: "Xarakter" },
  { slug: "style-ref", label: "Stil istinad" },
];
const TAG_LABEL = Object.fromEntries(SYSTEM_TAGS.map((t) => [t.slug, t.label]));

export default function AssetTagChips({ asset, onUpdated }) {
  const [adding, setAdding] = useState(false);
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  const tags = (asset && asset.tags) || [];

  useEffect(() => {
    if (adding && inputRef.current) inputRef.current.focus();
  }, [adding]);

  const suggestions = SYSTEM_TAGS.filter((t) => !tags.includes(t.slug));

  const saveTags = async (newTags) => {
    setSaving(true);
    try {
      await api.put(`/assets/${asset.id}/tags`, { tags: newTags });
      if (onUpdated) onUpdated();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const addTag = (slug) => {
    const s = slug.trim().toLowerCase();
    if (!s || tags.includes(s)) return;
    void saveTags([...tags, s]);
    setInput("");
    setAdding(false);
  };

  const removeTag = (slug) => { void saveTags(tags.filter((t) => t !== slug)); };

  return (
    <div className="mt-1.5" data-testid={`asset-tags-${asset.id}`}>
      <div className="flex flex-wrap gap-1">
        {tags.map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-violet-500/15 text-violet-300 border border-violet-500/30"
            data-testid={`tag-chip-${asset.id}-${t}`}
          >
            {TAG_LABEL[t] || t}
            <button
              onClick={() => removeTag(t)}
              className="ml-0.5 hover:text-rose-300 transition-colors"
              data-testid={`tag-remove-${asset.id}-${t}`}
              disabled={saving}
            >
              <X className="w-2.5 h-2.5" />
            </button>
          </span>
        ))}
        {!adding && tags.length < 10 && (
          <button
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-slate-800 text-slate-500 hover:text-slate-300 border border-slate-700 transition-colors"
            data-testid={`tag-add-btn-${asset.id}`}
          >
            <Plus className="w-2.5 h-2.5" /> Etiket
          </button>
        )}
      </div>
      {adding && (
        <div className="mt-1 relative" data-testid={`tag-input-area-${asset.id}`}>
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && input.trim()) addTag(input);
              if (e.key === "Escape") setAdding(false);
            }}
            placeholder="Etiket yazın…"
            className="w-full text-[11px] bg-slate-900 border border-slate-700 rounded px-2 py-1 text-slate-200 focus:outline-none focus:ring-1 focus:ring-violet-500/40"
            data-testid={`tag-input-${asset.id}`}
          />
          {suggestions.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {suggestions.map((s) => (
                <button
                  key={s.slug}
                  onClick={() => addTag(s.slug)}
                  className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400 hover:text-violet-300 hover:bg-violet-500/10 border border-slate-700 transition-colors"
                  data-testid={`tag-suggest-${asset.id}-${s.slug}`}
                >
                  + {s.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
